import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {righeDellaHome, sezioniDelleRighe, tuttiIGiochiDellaScheda} from '../../moduli/leggi';
import {giochi as giochiCasino, giochiPronti, descriviErrore as erroreCasino} from '../../services/casinoApi';
import {inAnteprima} from '../../skin';
import Nota from './Nota';
import GrigliaCelle from './GrigliaCelle';

/**
 * LE GRIGLIE DI GIOCHI (blocco «griglie» della Home): come le righe, ma
 * fitte. Per ogni scheda della lista (le stesse di «Righe di giochi»:
 * righeDellaHome) un titolo con «Vedi tutto» e il numero dei giochi, poi
 * le copertine su tante colonne quante ne stanno nello schermo (colonne.js:
 * da 3 sul telefono a 11 su un monitor da 32 pollici), per `righe` righe.
 * «Mostra di piu'» aggiunge altrettante righe, finche' ce ne sono; «Vedi
 * tutto» apre la pagina della scheda, a pagine.
 *
 * La griglia scrive le sue colonne da sola (repeat(n, 1fr)): cosi' l'ultima
 * riga visibile e' sempre piena, senza il buco a destra che lascia una
 * griglia auto-fill quando i giochi finiscono a meta' riga.
 */
export default function GriglieDiGiochi({scelte, righe, mostraDiPiu, colonne, larghezza}) {
  const {t} = useTranslation();
  const sezioni = sezioniDelleRighe();
  const lista = righeDellaHome(scelte, sezioni);
  const chiave = Array.from(new Set(lista.flatMap((r) => (r.sezione === 'home' ? r.scheda.servono : [r.sezione])))).join(',');
  const [stato, setStato] = React.useState(() => {
    const pronti = chiave ? chiave.split(',').map((k) => [k, giochiPronti(k)]) : [];
    return pronti.length && pronti.every(([, e]) => e)
      ? {carica: false, errore: '', elenchi: Object.fromEntries(pronti)}
      : {carica: !!chiave, errore: '', elenchi: {}};
  });
  React.useEffect(() => {
    if (!chiave) return undefined;
    let vivo = true;
    Promise.all(chiave.split(',').map((k) => giochiCasino(k).then((elenco) => [k, elenco])))
        .then((coppie) => vivo && setStato({carica: false, errore: '', elenchi: Object.fromEntries(coppie)}))
        .catch((e) => vivo && setStato({carica: false, errore: erroreCasino(e), elenchi: {}}));
    return () => { vivo = false; };
  }, [chiave]);

  if (!lista.length) {
    if (!inAnteprima()) return null;
    return (
      <section className={'vet-blocco'} aria-label={t('Griglie di giochi')}>
        <Nota tono={'spento'}>
          {t('Nessuna griglia da mostrare: si scelgono dal backoffice (Moduli › Home › Griglie di giochi), fra le schede di Casinò e Live casino.')}
        </Nota>
      </section>
    );
  }
  const perRiga = Math.max(1, Number(righe) || 2);
  return (
    <>
      {stato.errore && <Nota tono={'errore'}>{stato.errore}</Nota>}
      {lista.map(({sezione, scheda}) => {
        const elenchi = (sezione === 'home' ? scheda.servono : [sezione]).map((k) => [k, stato.elenchi[k] || []]);
        const giochi = tuttiIGiochiDellaScheda(scheda, elenchi.flatMap(([, e]) => e));
        if (!stato.carica && !stato.errore && !giochi.length && !inAnteprima()) return null;
        return (
          <Griglia key={sezione + ':' + scheda.id} sezione={sezione} scheda={scheda} giochi={giochi} elenchi={elenchi}
            carica={stato.carica} righe={perRiga} colonne={colonne} larghezza={larghezza} conAltre={mostraDiPiu !== false}/>
        );
      })}
    </>
  );
}

function Griglia({sezione, scheda, giochi, elenchi, carica, righe, colonne, larghezza, conAltre}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const apri = (g) => {
    const k = (elenchi.find(([, e]) => e.includes(g)) || [sezione])[0];
    navigate((k === 'liveCasino' ? '/live-casino' : '/casino') + '/gioco/' + encodeURIComponent(g.id));
  };
  const apriTutti = () => navigate(sezione === 'home'
    ? '/categoria/' + encodeURIComponent(scheda.id)
    : (sezione === 'liveCasino' ? '/live-casino' : '/casino') + '?scheda=' + encodeURIComponent(scheda.id));
  const stile = larghezza > 0 ? {maxWidth: larghezza + 'px', marginLeft: 'auto', marginRight: 'auto', width: '100%'} : undefined;
  return (
    <section className={'vet-blocco vet-griglia'} aria-label={scheda.nome} style={stile}>
      <div className={'vet-testata'}>
        <h2>
          <button type={'button'} className={'cas-riga-nome'} onClick={apriTutti}>
            {scheda.icona ? scheda.icona + ' ' : ''}{scheda.nome}
          </button>
        </h2>
        <div className={'vet-testata-dx'}>
          <button type={'button'} className={'vet-link vet-griglia-tutti'} onClick={apriTutti}>
            {t('Vedi tutto')}{giochi.length > 0 && <span className={'vet-griglia-n'}>({giochi.length})</span>}
          </button>
        </div>
      </div>
      {!carica && !giochi.length && (
        <Nota tono={'spento'}>
          {sezione === 'home'
            ? t('La categoria «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › Home › Righe di giochi).', {nome: scheda.nome})
            : t('La scheda «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › {{sezione}} › Schede).', {nome: scheda.nome, sezione: sezione === 'liveCasino' ? t('Live casino') : t('Casinò')})}
        </Nota>
      )}
      <GrigliaCelle giochi={giochi} carica={carica} righe={righe} colonne={colonne} conAltre={conAltre}
        ripartiDa={scheda.id} onApri={apri} onTutti={apriTutti}/>
    </section>
  );
}
